// Google Translate language selector — gives every visitor the ability to
// translate the entire OSRR site into 100+ languages covering all 200+ supported
// countries. Loads the official Google widget once, then exposes a styled
// dropdown that drives it via the `googtrans` cookie.

import { useEffect, useRef, useState } from "react";
import { Globe2, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Curated list of major languages — Google Translate supports many more,
// but this set covers the dominant languages across all OSRR territories.
const LANGUAGES = [
  { code: "en", label: "English",      native: "English" },
  { code: "es", label: "Spanish",      native: "Español" },
  { code: "fr", label: "French",       native: "Français" },
  { code: "de", label: "German",       native: "Deutsch" },
  { code: "it", label: "Italian",      native: "Italiano" },
  { code: "pt", label: "Portuguese",   native: "Português" },
  { code: "nl", label: "Dutch",        native: "Nederlands" },
  { code: "ru", label: "Russian",      native: "Русский" },
  { code: "uk", label: "Ukrainian",    native: "Українська" },
  { code: "pl", label: "Polish",       native: "Polski" },
  { code: "tr", label: "Turkish",      native: "Türkçe" },
  { code: "ar", label: "Arabic",       native: "العربية" },
  { code: "he", label: "Hebrew",       native: "עברית" },
  { code: "fa", label: "Persian",      native: "فارسی" },
  { code: "hi", label: "Hindi",        native: "हिन्दी" },
  { code: "bn", label: "Bengali",      native: "বাংলা" },
  { code: "ur", label: "Urdu",         native: "اردو" },
  { code: "ja", label: "Japanese",     native: "日本語" },
  { code: "ko", label: "Korean",       native: "한국어" },
  { code: "zh-CN", label: "Chinese (Simplified)",  native: "简体中文" },
  { code: "zh-TW", label: "Chinese (Traditional)", native: "繁體中文" },
  { code: "vi", label: "Vietnamese",   native: "Tiếng Việt" },
  { code: "th", label: "Thai",         native: "ไทย" },
  { code: "id", label: "Indonesian",   native: "Bahasa Indonesia" },
  { code: "ms", label: "Malay",        native: "Bahasa Melayu" },
  { code: "fil", label: "Filipino",    native: "Filipino" },
  { code: "sw", label: "Swahili",      native: "Kiswahili" },
  { code: "ha", label: "Hausa",        native: "Hausa" },
  { code: "yo", label: "Yoruba",       native: "Yorùbá" },
  { code: "zu", label: "Zulu",         native: "isiZulu" },
  { code: "af", label: "Afrikaans",    native: "Afrikaans" },
  { code: "sv", label: "Swedish",      native: "Svenska" },
  { code: "no", label: "Norwegian",    native: "Norsk" },
  { code: "da", label: "Danish",       native: "Dansk" },
  { code: "fi", label: "Finnish",      native: "Suomi" },
  { code: "is", label: "Icelandic",    native: "Íslenska" },
  { code: "el", label: "Greek",        native: "Ελληνικά" },
  { code: "cs", label: "Czech",        native: "Čeština" },
  { code: "sk", label: "Slovak",       native: "Slovenčina" },
  { code: "hu", label: "Hungarian",    native: "Magyar" },
  { code: "ro", label: "Romanian",     native: "Română" },
  { code: "bg", label: "Bulgarian",    native: "Български" },
  { code: "sr", label: "Serbian",      native: "Српски" },
  { code: "hr", label: "Croatian",     native: "Hrvatski" },
  { code: "sl", label: "Slovenian",    native: "Slovenščina" },
  { code: "lt", label: "Lithuanian",   native: "Lietuvių" },
  { code: "lv", label: "Latvian",      native: "Latviešu" },
  { code: "et", label: "Estonian",     native: "Eesti" },
  { code: "mt", label: "Maltese",      native: "Malti" },
  { code: "ca", label: "Catalan",      native: "Català" },
  { code: "eu", label: "Basque",       native: "Euskara" },
  { code: "gl", label: "Galician",     native: "Galego" },
];

function readGoogtrans() {
  // Cookie value is "/en/{lang}" — return the target lang or "en".
  const m = document.cookie.match(/(?:^|;\s*)googtrans=\/[a-z\-]+\/([a-zA-Z\-]+)/);
  return m ? m[1] : "en";
}

function setGoogtrans(lang) {
  // Cookie must be set on both the bare hostname and the dot-prefixed hostname
  // so that Google Translate picks it up on first load and reloads.
  const value = `/en/${lang}`;
  const host = window.location.hostname;
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `googtrans=${value}; expires=${expires}; path=/`;
  document.cookie = `googtrans=${value}; expires=${expires}; path=/; domain=${host}`;
  // Also set on apex domain (e.g. .example.com)
  const apex = host.split(".").slice(-2).join(".");
  document.cookie = `googtrans=${value}; expires=${expires}; path=/; domain=.${apex}`;
}

export default function LanguageSelector() {
  const [current, setCurrent] = useState("en");
  const initRef = useRef(false);

  useEffect(() => {
    setCurrent(readGoogtrans());
    if (initRef.current) return;
    initRef.current = true;

    // Inject Google Translate widget once.
    if (!document.getElementById("google_translate_element")) {
      const div = document.createElement("div");
      div.id = "google_translate_element";
      div.style.position = "absolute";
      div.style.left = "-10000px";
      div.style.top = "0";
      div.setAttribute("aria-hidden", "true");
      document.body.appendChild(div);
    }
    if (!window.googleTranslateElementInit) {
      window.googleTranslateElementInit = function () {
        // eslint-disable-next-line no-new, no-undef
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            autoDisplay: false,
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          },
          "google_translate_element",
        );
      };
    }
    if (!document.querySelector('script[data-gt-loaded]')) {
      const s = document.createElement("script");
      s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      s.setAttribute("data-gt-loaded", "1");
      document.body.appendChild(s);
    }
  }, []);

  const choose = (code) => {
    setGoogtrans(code);
    setCurrent(code);
    // Reload so Google Translate picks up the new cookie and translates fully.
    window.location.reload();
  };

  const currentLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="notranslate inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/10 bg-white/[0.02] hover:border-[#7DA3D9]/40 hover:bg-[#7DA3D9]/5 text-xs text-slate-300 hover:text-white transition-colors"
          title="Translate this site"
          data-testid="language-selector-trigger"
        >
          <Globe2 className="w-3.5 h-3.5 text-[#7DA3D9]" />
          <span className="hidden sm:inline">{currentLang.native}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="bg-[#0A1128]/95 backdrop-blur-xl border-white/10 max-h-[420px] overflow-y-auto min-w-[240px] p-1 notranslate"
        align="end"
        sideOffset={8}
        data-testid="language-selector-menu"
      >
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => choose(l.code)}
            className="flex items-center justify-between px-3 py-2 rounded-md cursor-pointer focus:bg-white/[0.04] data-[highlighted]:bg-white/[0.04]"
            data-testid={`lang-${l.code}`}
          >
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-white">{l.native}</span>
              {l.native !== l.label && (
                <span className="text-[10px] text-slate-500">{l.label}</span>
              )}
            </div>
            {current === l.code && <Check className="w-3.5 h-3.5 text-[#7DA3D9]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
